<script lang="ts">
  import { api } from '../../lib/api/client';
  import PeriodPicker from './PeriodPicker.svelte';
  import type { PlaceCategory, PlaceSummary } from '../../lib/api/types';
  import { t } from '../../lib/i18n/index.ts';
  import { categories, categoryIcon, categoryLabel, shortDate, span, timelineBase } from './format';

  // Places lists every place visited, grouped by category, with visits, time,
  // and last visit, sorted by most visited, most time, or most recent (005 FR-039).
  // On the member's own, each opens its page (spec 005 FR-021).
  let { groupId, userId, self, category = $bindable(''), start = '', onPlace }: {
    groupId?: string;
    userId: string;
    self: boolean;
    category?: PlaceCategory | '';
    // start is the year or month to open on, from Insights.
    start?: string;
    onPlace?: (p: PlaceSummary) => void;
  } = $props();

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let sort = $state('visits');
  // period is the from and to of the chosen stretch, empty for all time.
  let period = $state('');
  let places = $state<PlaceSummary[] | null>(null);
  let error = $state('');

  $effect(() => {
    load(sort, category, period);
  });

  async function load(s: string, c: string, range: string) {
    error = '';
    const q = new URLSearchParams({ tz, sort: s });
    if (c) q.set('category', c);
    try {
      places = await api.get<PlaceSummary[]>(`${timelineBase(groupId, userId, self)}/places?${q}${range}`);
    } catch (e: any) {
      error = e.message;
      places = null;
    }
  }

  // groups keeps the sort within each category, categories in their usual order.
  const groups = $derived(
    categories.map((c) => ({ category: c, places: (places ?? []).filter((p) => p.category === c) })).filter((g) => g.places.length > 0),
  );
</script>

<div class="flex-1 overflow-y-auto p-4 sm:p-6">
  <div class="max-w-3xl mx-auto space-y-4">
    <div class="flex items-center gap-3 flex-wrap">
      <PeriodPicker bind:query={period} {start} />
      <select class="input w-56" bind:value={category} aria-label={t('timelineCategoryLabel')}>
        <option value="">{t('timelineEveryCategory')}</option>
        {#each categories as c (c)}<option value={c}>{categoryLabel(c)}</option>{/each}
      </select>
      <select class="input w-48" bind:value={sort} aria-label={t('timelineSort')}>
        <option value="visits">{t('timelineSortVisits')}</option>
        <option value="time">{t('timelineSortTime')}</option>
        <option value="recent">{t('timelineSortRecent')}</option>
      </select>
      {#if places}<span class="text-sm text-text-muted">{t('timelinePlaceCount', { n: places.length })}</span>{/if}
    </div>

    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {:else if places && places.length === 0}
      <p class="text-sm text-text-muted">{t('timelineNoPlaces')}</p>
    {:else}
      {#each groups as g (g.category)}
        <section class="card p-5">
          <h2 class="font-semibold mb-2"><i aria-hidden="true" class="las {categoryIcon(g.category)} mr-1"></i>{categoryLabel(g.category)}</h2>
          <ul>
            {#each g.places as p, i (p.id ?? i)}
              <li>
                {#snippet row()}
                  <span class="flex-1 min-w-0">
                    <span class="block font-medium truncate">{p.name || p.address || `${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`}</span>
                    <span class="block text-xs text-text-muted truncate">{[p.name ? p.address : '', p.city].filter(Boolean).join(', ')}</span>
                  </span>
                  <span class="text-sm text-text-muted text-right shrink-0">
                    {t('timelineVisitsTime', { visits: p.visits, time: span(p.seconds) })}<br />{t('timelineLastVisit', { date: shortDate(p.last_visit) })}
                  </span>
                {/snippet}
                {#if onPlace && p.id}
                  <button class="w-full text-left py-2 flex items-center gap-3 hover:opacity-80" onclick={() => onPlace(p)}>
                    {@render row()}<i aria-hidden="true" class="las la-angle-right text-text-muted"></i>
                  </button>
                {:else}
                  <div class="py-2 flex items-baseline gap-3">{@render row()}</div>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/each}
    {/if}
  </div>
</div>
